import React, { useState, useMemo, useCallback } from 'react';
import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  TopToolbar,
  SearchInput,
  useGetOne,
} from 'react-admin';
import {
  Card,
  Typography,
  Box,
  Chip,
  IconButton,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Devices as DevicesIcon,
  Business as PlantIcon,
  Visibility as ShowIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import HierarchicalFilter, {
  HierarchicalFilterSelections,
} from '../../components/HierarchicalFilter';
import CustomCreateButton from '@/components/CustomCreateButton';
import CustomDeleteButton from '@/components/CustomDeleteButton';

const buttonStyle = {
  minWidth: '24px',
  width: '24px',
  height: '24px',
  padding: '2px',
  '& .MuiButton-startIcon': { margin: 0 },
  '& svg': { fontSize: '16px' }
};

// Componente para mostrar la planta con trazado completo
const PlantField = ({ record }: { record: any }) => {
  const tableId = record?.tableId;

  // Obtiene la tabla
  const { data: table, isLoading: isLoadingTable, error: tableError } = useGetOne(
    'tables',
    { id: tableId },
    { enabled: !!tableId }
  );

  // Obtener la zona
  const { data: zone, isLoading: isLoadingZone, error: zoneError } = useGetOne(
    'zones',
    { id: table?.zoneId },
    { enabled: !!table?.zoneId }
  );

  // Obtiene la planta
  const { data: plant, isLoading: isLoadingPlant, error: plantError } = useGetOne(
    'plants',
    { id: zone?.plantId },
    { enabled: !!zone?.plantId }
  );

  const isLoading = isLoadingTable || isLoadingZone || isLoadingPlant;
  const hasError = tableError || zoneError || plantError;

  if (!tableId) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <PlantIcon fontSize="small" color="disabled" />
        <span style={{ color: '#999' }}>Sin planta asociada</span>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Skeleton variant="circular" width={16} height={16} />
        <Skeleton variant="text" width={80} height={20} />
      </Box>
    );
  }

  if (hasError || !plant) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <PlantIcon fontSize="small" color="error" />
        <span style={{ color: '#f44336' }}>Error</span>
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <PlantIcon fontSize="small" color="primary" />
      <span>{plant.plantName || plant.name || `Planta ${plant.id}`}</span>
    </Box>
  );
};

// Botón de crear
const DeviceListActions = () => (
  <TopToolbar>
    <CustomCreateButton
      variant='contained'
      label='Crear Dispositivo'
      icon={<AddIcon />}
      sx={{
        height: '40px',
        backgroundColor: 'primary.main',
        '&:hover': { backgroundColor: 'primary.dark' },
      }}
    />
  </TopToolbar>
);

// Filtros de búsqueda 
const deviceFilters = [
  <SearchInput
    key='search'
    source='q'
    placeholder='Buscar dispositivos...'
    alwaysOn
    size='small'
    sx={{
      minWidth: 200,
      mb: 0.5
    }}
  />,
];

export const DevicesList: React.FC = () => {
  const [filterValues, setFilterValues] = useState<HierarchicalFilterSelections>({});
  const [pagination, setPagination] = useState({ page: 1, perPage: 5 });

  const handleFilterChange = useCallback((selections: HierarchicalFilterSelections) => {
    setFilterValues(selections);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  // Filter object por React Admin
  const filter = useMemo(() => {
    const filterObj: any = {};

    // Solo incluiye filtros si tienen valores
    if (filterValues.plantId) {
      filterObj.plantId = filterValues.plantId;
    }

    if (filterValues.zoneId) {
      filterObj.zoneId = filterValues.zoneId;
    }

    if (filterValues.tableId) {
      filterObj.tableId = filterValues.tableId;
    }

    // PAGINACIÓN SERVIDOR
    filterObj.isPaginated = true;
    filterObj.page = pagination.page;
    filterObj.pageSize = pagination.perPage;

    return filterObj;
  }, [filterValues.plantId, filterValues.zoneId, filterValues.tableId, pagination.page, pagination.perPage]);

  return (
    <Card elevation={1} sx={{ m: 2, p: 2 }}>
      <Box sx={{ p: 3, pt: 2, pb: 2.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography
          variant='h5'
          component='h1'
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <DevicesIcon color='primary' />
          Gestión de Dispositivos
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Gestiona los dispositivos del sistema organizados por plantas
        </Typography>
      </Box>

      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant='h6' gutterBottom color='primary'>
          Filtros de Búsqueda
        </Typography>

        {/* Filtro jerárquico */}
        <HierarchicalFilter
          levels={['plant']}
          onChange={handleFilterChange}
        />

        {/* Mostrar seleccion actual*/}
        {filterValues.plantId && (
          <Box sx={{ mt: 2 }}>
            <Typography variant='body2' color='text.secondary'>
              Selección actual: Planta: {filterValues.plantName}
            </Typography>
          </Box>
        )}

        {/* Indicador cuando hay filtros*/}
        {!filterValues.plantId && (
          <Box sx={{ mt: 2 }}>
            <Typography variant='body2' color='info.main'>
              ✓ Mostrando todos los dispositivos del sistema
            </Typography>
          </Box>
        )}
      </Box>

      {/* LISTA DE REACT ADMIN */}
      <List
        title={false}
        actions={<DeviceListActions />}
        filter={filter}
        filters={deviceFilters}
        perPage={pagination.perPage}
        sort={{ field: 'id', order: 'ASC' }}
        exporter={false}
        sx={{
          '& .RaList-main': { mt: 0, p: 0 },
          '& .RaDatagrid-rowCell, & .RaDatagrid-headerCell': {
            padding: '6px 8px',
          }
        }}
      >
        <Box sx={{
          width: '100%',
          overflowX: 'auto',
          pb: 1,
        }}>
          <Datagrid
            rowClick={false}
            sx={{
              '& .RaDatagrid-headerCell': { fontWeight: 'bold' },
              tableLayout: 'fixed',
              width: '100%',
            }}
          >
            {/* ID */}
            <TextField
              source="id"
              label="ID"
              sx={{ minWidth: 50, maxWidth: 50 }}
            />

            {/* Nombre Variable */}
            <TextField
              source="variableName"
              label="Nombre Variable"
              sx={{ maxWidth: 150 }}
            />

            {/* Planta - AHORA CON NOMBRE REAL */}
            <FunctionField
              label="Planta"
              render={(record: any) => <PlantField record={record} />}
              sx={{ maxWidth: 150 }}
            />

            {/* Dirección IP */}
            <FunctionField
              label="Dirección IP"
              render={(record: any) => (
                <Chip
                  label={record.ipAddress || 'Sin IP'}
                  color={record.ipAddress ? 'primary' : 'default'}
                  size="small"
                  variant="outlined"
                />
              )}
              sx={{ maxWidth: 120 }}
            />

            {/* Acciones */}
            <FunctionField
              label=""
              render={(record: any) => (
                <Box sx={{ 
                  display: 'flex', 
                  gap: '4px', 
                  justifyContent: 'center',
                  minWidth: '120px'
                }}>
                  <IconButton
                    size="small"
                    component={Link}
                    to={`/devices/${record.id}/show`}
                    sx={buttonStyle}
                    color="primary"
                  >
                    <ShowIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    component={Link}
                    to={`/devices/${record.id}`}
                    sx={buttonStyle}
                    color="secondary"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <CustomDeleteButton
                    record={record}
                    resource="devices"
                    nameField="variableName"
                    sx={buttonStyle}
                    label={false}
                  />
                </Box>
              )}
              sx={{ 
                minWidth: '120px',
                maxWidth: '120px',
                textAlign: 'center'
              }}
            />
          </Datagrid>
        </Box>
      </List>
    </Card>
  );
};

export default DevicesList;